import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import {
  TemporalCompareChangedError,
  exportTemporalCompare,
  type TemporalCompareExportFormat,
  type TemporalCompareGap,
  type TemporalCompareRequest,
} from "../../api/temporalCompare";
import { useTemporalCompare } from "./useTemporalCompare";
import "./proof-trace.css";

export interface TemporalComparePanelProps {
  request: TemporalCompareRequest;
  onClose: () => void;
  onReturnFocus?: () => void;
  expectedSemanticDigest?: string | null;
  initialSourceLocator?: string | null;
}

const INITIAL_CHANGE_COUNT = 20;
const CHANGE_PAGE_SIZE = 20;
const INITIAL_GAP_COUNT = 20;
const GAP_PAGE_SIZE = 20;

function humanize(value: string): string {
  return value.replace(/_/g, " ").replace(/^./, (letter: string) => letter.toLocaleUpperCase());
}

function shortIdentity(value: string): string {
  return value.length > 30 ? `${value.slice(0, 18)}…${value.slice(-8)}` : value;
}

function saveExport(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

interface ChangeSectionProps<T> {
  id: string;
  title: string;
  noun: string;
  items: T[];
  resetKey: string;
  getKey: (item: T, index: number) => string;
  renderItem: (item: T) => ReactNode;
}

function ChangeSection<T>({ id, title, noun, items, resetKey, getKey, renderItem }: ChangeSectionProps<T>) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_CHANGE_COUNT);

  useEffect(() => setVisibleCount(INITIAL_CHANGE_COUNT), [resetKey]);

  return (
    <section aria-labelledby={id} data-temporal-section={id}>
      <div className="proof-trace__section-heading"><h3 id={id}>{title}</h3><span>{items.length}</span></div>
      <p className="proof-trace__progress" aria-live="polite">
        Showing {Math.min(visibleCount, items.length)} of {items.length} {noun}
      </p>
      {items.length ? (
        <ol className="proof-trace__assertions">
          {items.slice(0, visibleCount).map((item, index) => <li key={getKey(item, index)}>{renderItem(item)}</li>)}
        </ol>
      ) : <p className="proof-trace__progress">No {noun}.</p>}
      {visibleCount < items.length ? (
        <button
          className="proof-trace__load-more"
          type="button"
          onClick={() => setVisibleCount((current) => Math.min(current + CHANGE_PAGE_SIZE, items.length))}
        >
          Show more {noun}
        </button>
      ) : null}
    </section>
  );
}

interface DisplayGap {
  side: "before" | "after" | "comparison";
  gap: TemporalCompareGap;
}

function causalContextLabel(count: number): string {
  return `${count} causal context${count === 1 ? "" : "s"}`;
}

export function TemporalComparePanel({
  request,
  onClose,
  onReturnFocus,
  expectedSemanticDigest = null,
  initialSourceLocator = null,
}: TemporalComparePanelProps) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const summaryRef = useRef<HTMLElement | null>(null);
  const { data, error, isLoading, refresh } = useTemporalCompare(request);
  const [exporting, setExporting] = useState<TemporalCompareExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [visibleGapCount, setVisibleGapCount] = useState(INITIAL_GAP_COUNT);
  const compareDocument = data?.document;
  const resetKey = data?.semantic_identity.digest ?? "none";
  const semanticDigestMismatch = Boolean(
    data && expectedSemanticDigest && data.semantic_identity.digest !== expectedSemanticDigest,
  );
  const gaps = useMemo<DisplayGap[]>(() => compareDocument ? [
    ...compareDocument.completeness.before.gaps.map((gap) => ({ side: "before" as const, gap })),
    ...compareDocument.completeness.after.gaps.map((gap) => ({ side: "after" as const, gap })),
    ...compareDocument.completeness.comparison.gaps.map((gap) => ({ side: "comparison" as const, gap })),
  ] : [], [compareDocument]);

  useEffect(() => closeRef.current?.focus(), []);
  useEffect(() => {
    setVisibleGapCount(INITIAL_GAP_COUNT);
    setExportError(null);
  }, [resetKey]);
  useEffect(() => {
    if (!data || semanticDigestMismatch || initialSourceLocator !== "/document/summary") return;
    summaryRef.current?.focus();
    summaryRef.current?.scrollIntoView({ block: "nearest" });
  }, [data, initialSourceLocator, semanticDigestMismatch]);

  const close = useCallback(() => {
    onClose();
    window.requestAnimationFrame(() => onReturnFocus?.());
  }, [onClose, onReturnFocus]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    close();
  };

  const download = async (format: TemporalCompareExportFormat) => {
    if (!data || exporting) return;
    const displayed = data.document;
    setExportError(null);
    setExporting(format);
    try {
      const result = await exportTemporalCompare({
        project_id: displayed.project_id,
        root_refs: displayed.scope.root_refs,
        before_as_of: displayed.before_as_of,
        after_as_of: displayed.after_as_of,
        recorded_as_of: displayed.recorded_as_of,
        snapshot_token: displayed.snapshot_token,
        direction: displayed.scope.direction,
        max_depth: displayed.scope.limits.max_depth,
        max_records: displayed.scope.limits.max_records,
        max_paths: displayed.scope.limits.max_paths,
        max_path_steps: displayed.scope.limits.max_path_steps,
        expected_semantic_digest: data.semantic_identity.digest,
      }, format);
      saveExport(result.blob, result.filename);
    } catch (exportFailure) {
      setExportError(exportFailure instanceof TemporalCompareChangedError
        ? "Comparison changed. Refresh it before downloading a new export."
        : exportFailure instanceof Error
          ? exportFailure.message
          : "Could not export this temporal comparison.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <aside
      className="proof-trace temporal-compare"
      role="complementary"
      aria-label="Time Compare changes"
      onKeyDown={onKeyDown}
      data-testid="temporal-compare-panel"
      data-time-compare-ready="true"
    >
      <header className="proof-trace__header">
        <div>
          <span>Bitemporal lineage delta</span>
          <h2 id={titleId}>Time Compare</h2>
        </div>
        <button ref={closeRef} type="button" onClick={close} aria-label="Close time comparison">×</button>
      </header>

      {isLoading ? <div className="proof-trace__state" role="status">Comparing exact temporal cuts…</div> : null}
      {error ? (
        <div className="proof-trace__state is-error" role="alert">
          <strong>Time comparison unavailable</strong><span>{error.message}</span><button type="button" onClick={refresh}>Try again</button>
        </div>
      ) : null}
      {semanticDigestMismatch ? (
        <div className="proof-trace__state is-error" role="alert">
          <strong>Cited comparison changed</strong><span>The reopened comparison does not match the authorized source digest.</span>
        </div>
      ) : null}

      {compareDocument && data && !semanticDigestMismatch ? (
        <>
          <div className="proof-trace__body">
            <section className="proof-trace__snapshot" aria-labelledby={`${titleId}-window`}>
              <h3 id={`${titleId}-window`}>Exact replay window</h3>
              <div className="proof-trace__completeness" aria-label="Temporal comparison completeness">
                <span className={`is-${compareDocument.completeness.before.status}`}><strong>Before</strong> {humanize(compareDocument.completeness.before.status)}</span>
                <span className={`is-${compareDocument.completeness.after.status}`}><strong>After</strong> {humanize(compareDocument.completeness.after.status)}</span>
                <span className={`is-${compareDocument.completeness.comparison.status}`}><strong>Comparison</strong> {humanize(compareDocument.completeness.comparison.status)}</span>
              </div>
              <dl>
                <div><dt>Before effective / event</dt><dd><time dateTime={compareDocument.before_as_of}>{compareDocument.before_as_of}</time></dd></div>
                <div><dt>After effective / event</dt><dd><time dateTime={compareDocument.after_as_of}>{compareDocument.after_as_of}</time></dd></div>
                <div><dt>Recorded / system as of</dt><dd><time dateTime={compareDocument.recorded_as_of}>{compareDocument.recorded_as_of}</time></dd></div>
                <div><dt>Snapshot token</dt><dd title={compareDocument.snapshot_token}>{shortIdentity(compareDocument.snapshot_token)}</dd></div>
                <div><dt>Roots</dt><dd title={compareDocument.scope.root_refs.map((root) => root.id).join(", ")}>{compareDocument.scope.root_refs.length} ArtifactVersion{compareDocument.scope.root_refs.length === 1 ? "" : "s"}</dd></div>
                <div><dt>Direction</dt><dd>{humanize(compareDocument.scope.direction)}</dd></div>
                <div><dt>Temporal basis</dt><dd>Effective and recorded time</dd></div>
                <div><dt>Bounds</dt><dd>{compareDocument.scope.limits.max_depth} deep · {compareDocument.scope.limits.max_records} records · {compareDocument.scope.limits.max_paths} paths · {compareDocument.scope.limits.max_path_steps} total steps</dd></div>
              </dl>
            </section>

            <section
              ref={summaryRef}
              className="proof-trace__snapshot is-grounded-highlight"
              aria-labelledby={`${titleId}-summary`}
              tabIndex={-1}
              data-grounded-source-locator={initialSourceLocator === "/document/summary" ? initialSourceLocator : undefined}
            >
              <h3 id={`${titleId}-summary`}>Change summary</h3>
              <dl aria-label="Temporal comparison summary">
                <div><dt>Topology added</dt><dd>{compareDocument.summary.topology_added}</dd></div>
                <div><dt>Topology removed</dt><dd>{compareDocument.summary.topology_removed}</dd></div>
                <div><dt>Supersessions added</dt><dd>{compareDocument.summary.supersessions_added}</dd></div>
                <div><dt>Validity changed</dt><dd>{compareDocument.summary.validity_changed}</dd></div>
                <div><dt>Decision status changed</dt><dd>{compareDocument.summary.decision_status_changed}</dd></div>
                <div><dt>Evidence added</dt><dd>{compareDocument.summary.evidence_added}</dd></div>
                <div><dt>Evidence removed</dt><dd>{compareDocument.summary.evidence_removed}</dd></div>
              </dl>
              <p className="proof-trace__redaction" role="note">
                Validity changes are state transitions. They are not topology removals; topology removals require separate typed retirement evidence.
              </p>
            </section>

            {gaps.length ? (
              <section className="proof-trace__gaps" aria-labelledby={`${titleId}-gaps`}>
                <h3 id={`${titleId}-gaps`}>Incomplete comparison</h3>
                <p>Every known boundary is stated explicitly; no missing delta is presented as certainty.</p>
                <p className="proof-trace__progress" aria-live="polite">Showing {Math.min(visibleGapCount, gaps.length)} of {gaps.length} gaps</p>
                <ol>
                  {gaps.slice(0, visibleGapCount).map(({ side, gap }, index) => (
                    <li key={`${side}:${gap.code}:${gap.subject?.entity_kind ?? ""}:${gap.subject?.id ?? ""}:${index}`}>
                      <strong>{humanize(side)} · {humanize(gap.code)}</strong>
                      <span title={gap.subject?.id}>{gap.subject ? `${humanize(gap.subject.entity_kind)} ${shortIdentity(gap.subject.id)}` : "Whole comparison"}</span>
                    </li>
                  ))}
                </ol>
                {visibleGapCount < gaps.length ? (
                  <button className="proof-trace__load-more" type="button" onClick={() => setVisibleGapCount((current) => Math.min(current + GAP_PAGE_SIZE, gaps.length))}>Show more gaps</button>
                ) : null}
              </section>
            ) : null}

            <ChangeSection
              id={`${titleId}-validity`}
              title="Validity state changes"
              noun="validity changes"
              items={compareDocument.validity_changes}
              resetKey={resetKey}
              getKey={(change) => change.artifact_version_id}
              renderItem={(change) => <>
                <header><div><span>Validity state change</span><h4 title={change.artifact_version_id}>{shortIdentity(change.artifact_version_id)}</h4></div><strong>{humanize(change.before_state)} → {humanize(change.after_state)}</strong></header>
                <div className="proof-trace__assertion-value">
                  <strong>{change.considered_valid_before ? "Valid" : "Not valid"} → {change.considered_valid_after ? "Valid" : "Not valid"}</strong>
                  <p>State changed; this ArtifactVersion was not removed from topology.</p>
                  <small>{causalContextLabel(change.causal_contexts.length)} · complete validity timeline</small>
                </div>
              </>}
            />

            <ChangeSection
              id={`${titleId}-topology-added`}
              title="Topology additions"
              noun="topology additions"
              items={compareDocument.topology.added}
              resetKey={resetKey}
              getKey={(change) => `${change.input_binding_id}:${change.output_binding_id}`}
              renderItem={(change) => <>
                <header><div><span>Topology addition</span><h4>{shortIdentity(change.from_artifact_version_id)} → {shortIdentity(change.to_artifact_version_id)}</h4></div><strong>Added</strong></header>
                <div className="proof-trace__assertion-value">Operation {shortIdentity(change.operation_id)} · {humanize(change.input_role)} → {humanize(change.output_role)} · {causalContextLabel(change.causal_contexts.length)}</div>
              </>}
            />

            <ChangeSection
              id={`${titleId}-topology-removed`}
              title="Topology removals"
              noun="topology removals"
              items={compareDocument.topology.removed}
              resetKey={resetKey}
              getKey={(change) => `${change.input_binding_id}:${change.output_binding_id}`}
              renderItem={(change) => <>
                <header><div><span>Topology removal</span><h4>{shortIdentity(change.from_artifact_version_id)} → {shortIdentity(change.to_artifact_version_id)}</h4></div><strong>{humanize(change.retirement_action)}</strong></header>
                <div className="proof-trace__assertion-value">Typed {humanize(change.removal_category)} retirement · operation {shortIdentity(change.operation_id)} · {causalContextLabel(change.causal_contexts.length)}</div>
              </>}
            />

            <ChangeSection
              id={`${titleId}-supersessions`}
              title="Supersessions added"
              noun="supersessions"
              items={compareDocument.supersessions_added}
              resetKey={resetKey}
              getKey={(change) => change.supersession_id}
              renderItem={(change) => <>
                <header><div><span>Supersession added</span><h4>{shortIdentity(change.old_artifact_version_id)} → {shortIdentity(change.new_artifact_version_id)}</h4></div><strong>{humanize(change.change_type)}</strong></header>
                <div className="proof-trace__assertion-value">{causalContextLabel(change.causal_contexts.length)}</div>
              </>}
            />

            <ChangeSection
              id={`${titleId}-decision-status`}
              title="Decision status changes"
              noun="decision status changes"
              items={compareDocument.decision_status_changes}
              resetKey={resetKey}
              getKey={(change) => change.decision_id}
              renderItem={(change) => <>
                <header><div><span>Decision status change</span><h4 title={change.decision_id}>{shortIdentity(change.decision_id)}</h4></div><strong>{humanize(change.before_status)} → {humanize(change.after_status)}</strong></header>
                <div className="proof-trace__assertion-value">Complete status timeline · {causalContextLabel(change.causal_contexts.length)}</div>
              </>}
            />

            <ChangeSection
              id={`${titleId}-evidence-added`}
              title="Evidence membership added"
              noun="evidence additions"
              items={compareDocument.evidence_membership.added}
              resetKey={resetKey}
              getKey={(change) => `${change.decision_id}:${change.ordinal}`}
              renderItem={(change) => <>
                <header><div><span>Evidence membership added</span><h4 title={change.decision_id}>{shortIdentity(change.decision_id)} · slot {change.ordinal}</h4></div><strong>Added</strong></header>
                <div className="proof-trace__assertion-value">ArtifactVersion {shortIdentity(change.artifact_version_id)} · {causalContextLabel(change.causal_contexts.length)}</div>
              </>}
            />

            <ChangeSection
              id={`${titleId}-evidence-removed`}
              title="Evidence membership removed"
              noun="evidence removals"
              items={compareDocument.evidence_membership.removed}
              resetKey={resetKey}
              getKey={(change) => `${change.decision_id}:${change.ordinal}`}
              renderItem={(change) => <>
                <header><div><span>Evidence membership removed</span><h4 title={change.decision_id}>{shortIdentity(change.decision_id)} · slot {change.ordinal}</h4></div><strong>{humanize(change.retirement_action)}</strong></header>
                <div className="proof-trace__assertion-value">Typed decision evidence retirement · ArtifactVersion {shortIdentity(change.artifact_version_id)} · {causalContextLabel(change.causal_contexts.length)}</div>
              </>}
            />
          </div>

          <footer className="proof-trace__footer">
            <div><span>Displayed semantic digest</span><code title={data.semantic_identity.digest}>{shortIdentity(data.semantic_identity.digest)}</code></div>
            {exportError ? <p role="alert">{exportError} <button type="button" onClick={() => { setExportError(null); refresh(); }}>Refresh comparison</button></p> : null}
            <div className="proof-trace__exports">
              <button type="button" disabled={exporting !== null} onClick={() => void download("json")}>{exporting === "json" ? "Preparing JSON…" : "Download JSON"}</button>
              <button type="button" disabled={exporting !== null} onClick={() => void download("html")}>{exporting === "html" ? "Preparing print view…" : "Printable HTML"}</button>
            </div>
          </footer>
        </>
      ) : null}
    </aside>
  );
}
